import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, Upload, Send, Users, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface RecipientUploadResponse {
  success: boolean;
  recipients: string[];
  count: number;
  errors?: string[];
}

interface SendEmailResponse {
  success: boolean;
  sent: number;
  failed: number;
  errors?: Array<{ email: string; error: string }>;
}

export default function SendEmailsTab() {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [manualEmails, setManualEmails] = useState("");
  const [senderEmail, setSenderEmail] = useState("info@prizedrop.co.za");
  const [tenant, setTenant] = useState("");
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [sendResult, setSendResult] = useState<SendEmailResponse | null>(null);

  // Upload recipients mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await apiRequest("POST", "/api/admin/emails/parse-recipients", {
        body: formData,
      });
      return response.json() as Promise<RecipientUploadResponse>;
    },
    onSuccess: (data) => {
      if (data.success) {
        setRecipients(data.recipients);
        setUploadErrors(data.errors || []);
        toast({
          title: "Recipients Uploaded",
          description: `Successfully parsed ${data.count} email addresses.`,
        });
      } else {
        toast({
          title: "Upload Failed",
          description: "Failed to parse recipients file.",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Upload Error",
        description: error.message || "Failed to upload recipients file.",
        variant: "destructive",
      });
    },
  });

  // Send emails mutation
  const sendMutation = useMutation({
    mutationFn: async (recipientList: string[]) => {
      const response = await apiRequest("POST", "/api/admin/emails/send-bulk", {
        body: {
          recipients: recipientList,
          senderEmail,
          tenant: tenant || undefined,
          subject,
          emailBody,
        }
      });
      return response.json() as Promise<SendEmailResponse>;
    },
    onSuccess: (data) => {
      setSendResult(data);
      if (data.success) {
        toast({
          title: "Emails Sent Successfully",
          description: `Sent ${data.sent} emails. ${data.failed > 0 ? `${data.failed} failed.` : ""}`,
        });
        
        // Reset form after successful send
        if (data.failed === 0) {
          setSelectedFile(null);
          setRecipients([]);
          setManualEmails("");
          setSubject("");
          setEmailBody("");
          setTenant("");
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      } else {
        toast({
          title: "Send Failed",
          description: `Failed to send emails. ${data.failed} failed.`,
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Send Error",
        description: error.message || "Failed to send emails.",
        variant: "destructive",
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (fileExtension === "csv" || fileExtension === "txt") {
        setSelectedFile(file);
        setSendResult(null); // Clear previous send results
      } else {
        toast({
          title: "Invalid File Type",
          description: "Please upload a CSV or TXT file.",
          variant: "destructive",
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile);
    }
  };

  // Parse manual emails from textarea
  const parseManualEmails = (text: string): string[] => {
    if (!text.trim()) return [];
    
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const matches = text.match(emailRegex) || [];
    
    // Remove duplicates
    return Array.from(new Set(matches));
  };

  const handleSend = () => {
    // Validate and show specific error messages
    const missingFields: string[] = [];
    
    // Parse manual emails if provided
    const manualRecipients = parseManualEmails(manualEmails);
    const allRecipients = recipients.length > 0 ? recipients : manualRecipients;
    
    if (allRecipients.length === 0) {
      missingFields.push("Recipients (either upload a file or type emails manually)");
    }
    if (!senderEmail.trim()) {
      missingFields.push("Sender Email");
    }
    if (!subject.trim()) {
      missingFields.push("Subject Line");
    }
    if (!emailBody.trim()) {
      missingFields.push("Email Body");
    }

    if (missingFields.length > 0) {
      toast({
        title: "Missing Required Fields",
        description: `Please fill in: ${missingFields.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    // All validation passed, send emails with the appropriate recipient list
    sendMutation.mutate(allRecipients);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Builder
          </CardTitle>
          <CardDescription>
            Test sending emails or bulk emails from AWS SES
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Recipients Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5" />
              <Label className="text-base font-semibold">Recipients</Label>
            </div>
            
            <div>
              <Label htmlFor="recipients-file" className="text-sm">
                Upload Recipients (CSV/TXT)
              </Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  ref={fileInputRef}
                  id="recipients-file"
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileChange}
                  className="flex-1"
                  data-testid="input-recipients-file"
                />
                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploadMutation.isPending}
                  data-testid="button-upload"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {uploadMutation.isPending ? "Uploading..." : "Upload"}
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Upload a CSV file with emails or a TXT file with one email per line
              </p>
            </div>

            {/* Manual Email Input */}
            <div>
              <Label htmlFor="manual-emails" className="text-sm">
                Or Type Emails Manually (One per line or comma-separated)
              </Label>
              <Textarea
                id="manual-emails"
                value={manualEmails}
                onChange={(e) => {
                  setManualEmails(e.target.value);
                  // Clear uploaded recipients when typing manually
                  if (e.target.value.trim() && recipients.length > 0) {
                    setRecipients([]);
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }
                }}
                placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
                className="mt-1 min-h-[100px] font-mono text-sm"
                data-testid="textarea-manual-emails"
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter email addresses separated by commas, spaces, or new lines
              </p>
            </div>

            {/* Recipients Count */}
            {recipients.length > 0 && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{recipients.length}</strong> recipients loaded from file and ready to send
                </AlertDescription>
              </Alert>
            )}
            
            {/* Manual Recipients Count */}
            {manualEmails.trim() && parseManualEmails(manualEmails).length > 0 && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{parseManualEmails(manualEmails).length}</strong> email{parseManualEmails(manualEmails).length !== 1 ? "s" : ""} detected from manual input
                </AlertDescription>
              </Alert>
            )}

            {/* Upload Errors */}
            {uploadErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-semibold mb-1">Some emails could not be parsed:</p>
                  <ul className="text-sm list-disc list-inside">
                    {uploadErrors.slice(0, 5).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                    {uploadErrors.length > 5 && (
                      <li>...and {uploadErrors.length - 5} more</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Email Content Section */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-5 w-5" />
              <Label className="text-base font-semibold">Email Content</Label>
            </div>

            {/* Sender Email */}
            <div>
              <Label htmlFor="sender-email" className="text-sm flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Sender Email
              </Label>
              <Input
                id="sender-email"
                type="email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                placeholder="info@prizedrop.co.za"
                className="mt-1"
                data-testid="input-sender-email"
              />
              <p className="text-sm text-gray-500 mt-1">
                This email must be verified in Amazon SES
              </p>
            </div>

            {/* Tenant (Optional) */}
            <div>
              <Label htmlFor="tenant" className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4" />
                Tenant (Optional)
              </Label>
              <Input
                id="tenant"
                type="text"
                value={tenant}
                onChange={(e) => setTenant(e.target.value)}
                placeholder="tenant-name"
                className="mt-1"
                data-testid="input-tenant"
              />
              <p className="text-sm text-gray-500 mt-1">
                Specify tenant/organization for tracking purposes
              </p>
            </div>

            {/* Subject Line */}
            <div>
              <Label htmlFor="subject" className="text-sm">
                Subject Line
              </Label>
              <Input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject"
                className="mt-1"
                data-testid="input-subject"
              />
            </div>

            {/* Email Body */}
            <div>
              <Label htmlFor="email-body" className="text-sm">
                Email Body
              </Label>
              <Textarea
                id="email-body"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                placeholder="Enter your email message here..."
                className="mt-1 min-h-[200px]"
                data-testid="textarea-email-body"
              />
              <p className="text-sm text-gray-500 mt-1">
                Plain text email content
              </p>
            </div>
          </div>

          {/* Send Result */}
          {sendResult && (
            <Alert variant={sendResult.success && sendResult.failed === 0 ? "default" : "destructive"}>
              {sendResult.success && sendResult.failed === 0 ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                <p className="font-semibold mb-1">
                  Send Results: {sendResult.sent} sent, {sendResult.failed} failed
                </p>
                {sendResult.errors && sendResult.errors.length > 0 && (
                  <ul className="text-sm list-disc list-inside mt-2">
                    {sendResult.errors.slice(0, 5).map((error, idx) => (
                      <li key={idx}>
                        {error.email}: {error.error}
                      </li>
                    ))}
                    {sendResult.errors.length > 5 && (
                      <li>...and {sendResult.errors.length - 5} more errors</li>
                    )}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Send Button */}
          <div className="pt-4">
            <Button
              onClick={handleSend}
              disabled={sendMutation.isPending}
              size="lg"
              className="w-full"
              data-testid="button-send"
            >
              {sendMutation.isPending ? (
                <>
                  <Send className="h-4 w-4 mr-2 animate-pulse" />
                  Sending...
                </>
              ) : (() => {
                const manualRecipients = parseManualEmails(manualEmails);
                const totalRecipients = recipients.length > 0 ? recipients.length : manualRecipients.length;
                
                return totalRecipients > 0 ? (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send to {totalRecipients} recipient{totalRecipients !== 1 ? "s" : ""}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Email
                  </>
                );
              })()}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
